import { useEffect } from "react";
import { useSettings } from "../context/SettingsContext";

/**
 * Points the installed-app icon and title at THIS tenant instead of the
 * generic HostSync placeholder.
 *
 * Two different mechanisms, because the platforms disagree:
 *
 *  - Android/Chrome reads the web app manifest, which the backend now serves
 *    per tenant (backend/routes/pwaManifest.js). All this does there is swap
 *    the <link rel="manifest"> href to the per-tenant URL.
 *
 *  - iOS/Safari ignores the manifest for the home-screen icon entirely and
 *    uses <link rel="apple-touch-icon">. That lives in the static index.html,
 *    which nginx serves byte-identically to every tenant host, so it cannot
 *    be templated per tenant — it has to be rewritten in the DOM. Safari
 *    reads these tags when the user taps "Add to Home Screen", so updating
 *    them before that point is enough.
 *
 * index.html keeps its static defaults, so a tenant with no logo, or a page
 * hit before settings load, still gets a valid icon rather than none.
 */
function setLink(rel, href, extra = {}) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
}

export default function useTenantPwaIcons() {
  const { hotelName, logoUrl } = useSettings();

  useEffect(() => {
    // Per-tenant manifest (Android/Chrome install).
    setLink("manifest", "/api/manifest.webmanifest");

    if (logoUrl) {
      // Point iOS at the SERVER-GENERATED icon, not the raw logo file.
      // /api/pwa-icon/192.png is already square, already 192px, and already
      // has its alpha flattened to white (a transparent PNG renders black on
      // an iOS home screen). Handing Safari the raw upload instead meant a
      // wide logo got squashed into a square, and iOS and Android could show
      // two different icons for the same property. It also means the
      // platform-set-vs-self-uploaded logo precedence lives in one place,
      // server-side, rather than being duplicated here.
      setLink("apple-touch-icon", "/api/pwa-icon/192.png");
      setLink("icon", "/api/pwa-icon/192.png");
    }
  }, [logoUrl]);

  useEffect(() => {
    if (!hotelName) return;
    // The name iOS pre-fills in the "Add to Home Screen" sheet.
    let el = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!el) {
      el = document.createElement("meta");
      el.name = "apple-mobile-web-app-title";
      document.head.appendChild(el);
    }
    el.content = hotelName;
  }, [hotelName]);
}
